/**
 * PalladiumViewport — JS ↔ Blazor viewport bridge
 *
 * Tracks window size and breakpoint in real-time.
 * Sends updates to a Blazor .NET object via DotNetObjectReference.
 *
 * Breakpoints (Bootstrap 5):
 *   sm: < 576px   (mobile portrait)
 *   md: < 768px   (mobile landscape / small tablet)
 *   lg: < 992px   (tablet)
 *   xl: < 1200px  (desktop)
 *   xxl: >= 1200px (large desktop)
 */
(function () {
    'use strict';

    var _dotNetRef = null;
    var _pending = null;
    var _lastBreakpoint = '';
    var _lastOrientation = '';

    function getBreakpoint(w) {
        if (w < 576) return 'sm';
        if (w < 768) return 'md';
        if (w < 992) return 'lg';
        if (w < 1200) return 'xl';
        return 'xxl';
    }

    function getOrientation() {
        return window.innerWidth >= window.innerHeight ? 'landscape' : 'portrait';
    }

    function notify() {
        if (!_dotNetRef) return;

        var w = window.innerWidth;
        var h = window.innerHeight;
        var bp = getBreakpoint(w);
        var orientation = getOrientation();

        // Only round-trip to Blazor when the BREAKPOINT or ORIENTATION actually
        // changes. A Blazor Server re-render diffs the whole page; firing it on
        // every pixel resize re-diffs the live DOM repeatedly, and if anything
        // (a custom element upgrading, a lifted <style>, page CustomJs) has
        // touched a Blazor-tracked node, the diff throws "insertBefore/removeChild
        // of null" and kills the circuit (random white page). On mobile this is
        // acute: the URL-bar show/hide spams 'resize' on every scroll. Responsive
        // widgets only read the breakpoint, so intra-breakpoint resizes need no
        // re-render. First call fires (last* start empty).
        if (bp === _lastBreakpoint && orientation === _lastOrientation) return;
        _lastBreakpoint = bp;
        _lastOrientation = orientation;

        try {
            _dotNetRef.invokeMethodAsync('OnViewportChanged', w, h, bp, orientation);
        } catch (e) {
            // Circuit disposed — clean up
            _dotNetRef = null;
        }
    }

    function onResize() {
        if (_pending) cancelAnimationFrame(_pending);
        _pending = requestAnimationFrame(function () {
            _pending = null;
            notify();
        });
    }

    window.PalladiumViewport = {
        /**
         * Called from Blazor to start tracking.
         * @param {DotNetObjectReference} dotNetRef
         */
        init: function (dotNetRef) {
            _dotNetRef = dotNetRef;
            window.addEventListener('resize', onResize);
            window.addEventListener('orientationchange', function () {
                // orientationchange fires before resize on some devices
                setTimeout(onResize, 100);
            });
            // Send initial state
            notify();
        },

        /** Called from Blazor on dispose */
        dispose: function () {
            _dotNetRef = null;
            window.removeEventListener('resize', onResize);
        },

        /** Sync read (no interop needed) */
        getCurrent: function () {
            var w = window.innerWidth;
            var h = window.innerHeight;
            return {
                width: w,
                height: h,
                breakpoint: getBreakpoint(w),
                orientation: getOrientation(),
            };
        },
    };
})();
